'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { ProductCardV2Brand } from '@packages/modules/src/ProductCardV2/ProductCardV2.Brand';
import { ProductCardV2EfficiencyFlagsContainer } from '@packages/modules/src/ProductCardV2/ProductCardV2.EfficiencyFlagsContainer';
import { ProductCardV2EnergyEfficiencyFlagsLegacy } from '@packages/modules/src/ProductCardV2/ProductCardV2.EnergyEfficiencyFlagsLegacy';
import { ProductCardGridItem } from '@packages/modules/src/ProductCardV2/ProductCardV2.GridItem.client';
import { ProductCardV2Image } from '@packages/modules/src/ProductCardV2/ProductCardV2.Image';
import { ProductCardV2Name } from '@packages/modules/src/ProductCardV2/ProductCardV2.Name';
import { ProductCardV2Price } from '@packages/modules/src/ProductCardV2/ProductCardV2.Price.client';
import { ProductCardV2WishlistButton } from '@packages/modules/src/ProductCardV2/ProductCardV2.WishlistButton';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinProductClick } from '@packages/tracking/src/types/events';
import { useIntl } from 'react-intl';
import { getFormattedProduct } from './helper/getFormattedProduct';

/* GraphQL */ `
  fragment RecoProductCard on ProductRecommendation {
    product {
      akl
      name(type: NO_BRAND)
      orderNumber
      promotion
      sku
      link
      brand {
        name
      }
      downloads {
        link
        type
      }
      media {
        type
        url
      }
      powerEfficiencyFlags {
        color
        hasOldLabel
        level
        link
        text
      }
      price {
        amount
        currency
        isStartPrice
        saving {
          type
          value
        }
        uvp
        unit
        value
        valueOld
        valueUnit
      }
      tireEfficiency {
        flags {
          level
          type
        }
        link
      }
    }
    tracking {
      adId
      token
      trackingUrls {
        url
        purpose
      }
      logic
    }
  }
`;

export type RecoProductCardProps = {
  data: FragmentType<typeof RecoProductCardFragmentDoc>;
  /**
   * Recommendation type abbreviation, e.g. "prudsys_dv"
   */
  rdeTypeAbbr?: string;
  /**
   * Position of product in the recommendation list, starting with 1.
   */
  position: number;
  /**
   * Event handler for when a product is clicked.
   * This function is called when a user clicks a product element.
   */
  onClick?: (akl: string, sku: string) => void;
  className?: string;
};

export const RecoProductCard = ({
  data,
  rdeTypeAbbr,
  position,
  className,
  onClick,
}: RecoProductCardProps) => {
  const { product, tracking } = unmask<typeof RecoProductCardFragmentDoc>(data);
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();

  if (!product) return null;

  const {
    imageUrl,
    price,
    productId,
    productLink,
    energyEfficiencyFlags,
    tireEfficiencyFlags,
    legacyEfficiencyFlags,
  } = getFormattedProduct(product, tracking, formatMessage);

  return (
    <ProductCardGridItem
      className={`bg-color-background-base mb-0 [&>:last-child]:mb-0.5 lg:[&>:last-child]:mb-1 ${className}`}
      title={product?.name}
      id={productId}
      onClick={() => {
        dispatchGTMEvent<GTMEventGlycerinProductClick>({
          event: 'ProductClick',
          ProductClickData: {
            category: 'product-click',
            productId: product.akl,
            articleId: product.orderNumber,
            detail: `${product.brand?.name} ${product.name}`,
            skuId: product.sku,
            recoId: rdeTypeAbbr,
            listPosition: position,
            ...(tracking?.adId && { custom: { adId: tracking.adId } }),
          },
        });
        onClick?.(product.akl, product.sku);
      }}
    >
      <ProductCardV2Name id={productId} href={productLink} className="mx-1 line-clamp-2">
        {product.name}
      </ProductCardV2Name>
      <ProductCardV2Brand id={productId} className="mx-1 mt-1 line-clamp-1">
        {product.brand?.name}
      </ProductCardV2Brand>

      <ProductCardV2Image
        src={imageUrl}
        alt=""
        className="bg-color-background-base aspect-square p-0.5"
        imageClassName="rounded-t-sm"
      />
      <ProductCardV2WishlistButton
        size="medium"
        productName={product.name}
        sku={product.sku}
        wishListTrackingProps={{ products: [] }}
      />
      <ProductCardV2Price id={productId} price={price} className="group price-size-small mx-1" />
      {((energyEfficiencyFlags && energyEfficiencyFlags?.length > 0) ||
        (tireEfficiencyFlags && tireEfficiencyFlags?.length > 0)) && (
        <ProductCardV2EfficiencyFlagsContainer>
          {/** New flags are disabled until EEK flag can be assigned to corresponding datasheet */}
          {/* {energyEfficiencyFlags && energyEfficiencyFlags.length > 0 && (
            <ProductCardV2EnergyEfficiencyFlags flags={energyEfficiencyFlags} />
          )}
          {tireEfficiencyFlags && tireEfficiencyFlags.length > 0 && (
            <ProductCardV2TireEfficiencyFlags flags={tireEfficiencyFlags} />
          )} */}
          {legacyEfficiencyFlags && (
            <ProductCardV2EnergyEfficiencyFlagsLegacy {...legacyEfficiencyFlags} />
          )}
        </ProductCardV2EfficiencyFlagsContainer>
      )}
    </ProductCardGridItem>
  );
};
